import React from "react";

import { Link, useSearchParams } from "react-router-dom";

import "./RegistrationHomebuyer.css";

import backgroundImg from "../../../../assets/background.png";

import logoImg from "../../../../assets/logo.png";

import {

  EUROPEAN_COUNTRIES,

  PROPERTY_PURCHASE_COUNTRIES,

  getPhoneCodeByCountry,

} from "../../../../data/countries";

import { useTranslation } from "react-i18next";

import LanguageSwitcher from "../../../../components/LanguageSwitcher";

import LoginButton from "../../../../components/layout/LoginButton";

import { useAuth0 } from "@auth0/auth0-react";

import PasswordStrengthChecklist, {

  isStrongPassword,

} from "../../../../components/PasswordStrengthChecklist";

import { Eye, EyeOff, ShieldCheck, LoaderCircle } from "lucide-react";

import TermsConditionsModal from "../../legal/TermsConditionsModal";
import {
  normalizeLocalPhone,
  normalizeNationalId,
  getHomebuyerRegistrationErrors,
} from "../../../../utils/registrationValidation";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const REGISTRATION_STORAGE_KEY = "legup_hb_registration_form";

const AUTH_CONNECTION_KEY = "legup_auth_connection";

const HOMEBUYER_AUTH_CONNECTION = "LEGUP-Homebuyers";

/* Section */

const DEFAULT_FORM = {

  registration_concerns: "ONE_PERSON",

  family_situation: "SINGLE",

  first_name: "",

  last_name: "",

  country_of_residence: "",

  country_of_property_purchase: "",

  national_identification_number: "",

  phone: "",

  email: "",

  confirm_email: "",

  password: "",

  confirm_password: "",

  agree_terms: false,

};

/* Section */

const NATIONAL_ID_EXAMPLES = {

  BE: "11 digits (e.g. 85010112387)",

  ES: "DNI: 12345678Z or NIE: X1234567L",

  GR: "e.g. AB12345678",

  LU: "13 digits",

};

function normalizeInvitationPhone(value, countryCode) {
  return normalizeLocalPhone(
    value,
    countryCode,
    getPhoneCodeByCountry(countryCode),
  );
}

/* Section */

export default function RegisterHBPage() {

  const { t } = useTranslation("homebuyer-registration");

  const { loginWithRedirect } = useAuth0();

  const [searchParams] = useSearchParams();

/* Section */

  const invitationToken = String(searchParams.get("invitation") || "").trim();

  const isInvitationRegistration = Boolean(invitationToken);

/* Section */

  const [form, setForm] = React.useState(() => {

    try {

      const saved = localStorage.getItem(REGISTRATION_STORAGE_KEY);

      if (!saved) {

        return {

          ...DEFAULT_FORM,

        };

      }

      return {

        ...DEFAULT_FORM,

        ...JSON.parse(saved),

      };

    } catch {

      return {

        ...DEFAULT_FORM,

      };

    }

  });

/* Section */

  const [loading, setLoading] = React.useState(false);

  const [loadingInvitation, setLoadingInvitation] = React.useState(

    isInvitationRegistration,

  );

  const [invitationData, setInvitationData] = React.useState(null);

  const [invitationError, setInvitationError] = React.useState("");

  const [err, setErr] = React.useState("");

  const [ok, setOk] = React.useState("");

  const [fieldErrors, setFieldErrors] = React.useState({});

  const [showPassword, setShowPassword] = React.useState(false);

  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  const [termsModalOpen, setTermsModalOpen] = React.useState(false);

/* Section */

  const closeTermsModal = React.useCallback(() => {

    setTermsModalOpen(false);

  }, []);

/* Section */

  React.useEffect(() => {

    if (!invitationToken) {

      setLoadingInvitation(false);

      return;

    }

    let cancelled = false;

    async function loadInvitation() {

      try {

        setLoadingInvitation(true);

        setInvitationError("");

        setErr("");

        const response = await fetch(

          `${API_BASE}/api/homeowner/invitations/${encodeURIComponent(

            invitationToken,

          )}/validate/`,

          {

            method: "GET",

            headers: {

              Accept: "application/json",

            },

          },

        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {

          throw new Error(

            data?.detail ||

              t(

                "invitation.invalid",

                "This invitation is not valid or is no longer active.",

              ),

          );

        }

        if (cancelled) {

          return;

        }

        setInvitationData(data);

        const prefill = data?.registration_prefill || {};

        const residenceCountry = String(prefill?.country_of_residence || "")

          .trim()

          .toUpperCase();

        const propertyCountry = String(

          prefill?.country_of_property_purchase || "",

        )

          .trim()

          .toUpperCase();

        const invitedEmail = String(prefill?.email || data?.email || "")

          .trim()

          .toLowerCase();

/* Section */

        setForm((previous) => ({

          ...previous,

          registration_concerns: prefill?.registration_concerns || "ONE_PERSON",

          family_situation: prefill?.family_situation || "SINGLE",

          first_name: prefill?.first_name || data?.first_name || "",

          last_name: prefill?.last_name || data?.last_name || "",

          country_of_residence: residenceCountry,

          country_of_property_purchase: propertyCountry,

          national_identification_number:

            prefill?.national_identification_number || "",

          phone: normalizeInvitationPhone(prefill?.phone, residenceCountry),

          email: invitedEmail,

          confirm_email: invitedEmail,

/* Section */

          password: "",

          confirm_password: "",

/* Section */

          agree_terms: false,

        }));

        setFieldErrors({});

      } catch (requestError) {

        if (cancelled) {

          return;

        }

        const message =

          requestError?.message ||

          t(

            "invitation.loadFailed",

            "The invitation information could not be loaded.",

          );

        setInvitationError(message);

        setErr(message);

      } finally {

        if (!cancelled) {

          setLoadingInvitation(false);

        }

      }

    }

    loadInvitation();

    return () => {

      cancelled = true;

    };

  }, [invitationToken, t]);

/* Section */

  function onChange(e) {

    const { name, value, type, checked } = e.target;

    let nextValue = type === "checkbox" ? checked : value;

/* Section */

    if (

      isInvitationRegistration &&

      (name === "first_name" ||

        name === "last_name" ||

        name === "email" ||

        name === "confirm_email" ||

        name === "country_of_property_purchase")

    ) {

      return;

    }

/* Section */

    if (name === "phone") {
      nextValue = normalizeLocalPhone(
        value,
        form.country_of_residence,
        getPhoneCodeByCountry(form.country_of_residence),
      );
    }

    if (name === "national_identification_number") {
      nextValue = normalizeNationalId(value, form.country_of_residence);
    }

    setForm((previous) => ({

      ...previous,

      [name]: nextValue,

    }));

    setFieldErrors((previous) => ({

      ...previous,

      [name]: "",
      ...(name === "country_of_residence" ? {
        phone: "",
        national_identification_number: "",
      } : {}),
      ...(name === "email" ? {confirm_email: ""} : {}),
      ...(name === "password" ? {confirm_password: ""} : {}),

    }));

    if (err) {

      setErr("");

    }

  }

/* Section */

  // React onBlur bubbles from each input to the form.
  // Validate only the touched field; submit validates the entire form.
  function handleFieldBlur(event) {
    const name = event.target.name;
    if (!name || name === "registration_concerns" || name === "family_situation") return;
    if (isInvitationRegistration && event.target.readOnly) return;

    const value = event.target.type === "checkbox"
      ? event.target.checked
      : event.target.value ?? form[name];
    const current = { ...form, [name]: value };
    if (name === "phone") {
      current.phone = normalizeLocalPhone(
        current.phone, current.country_of_residence,
        getPhoneCodeByCountry(current.country_of_residence),
      );
    }
    const errors = getHomebuyerRegistrationErrors(current, isStrongPassword);
    setFieldErrors((previous) => ({
      ...previous,
      [name]: errors[name] ? t(errors[name]) : "",
    }));
  }

  function validateForm() {
    const errors = getHomebuyerRegistrationErrors(form, isStrongPassword);
    return Object.fromEntries(
      Object.entries(errors).map(([field, key]) => [field, t(key)]),
    );
  }

  function goToLogin() {

    sessionStorage.setItem(AUTH_CONNECTION_KEY, HOMEBUYER_AUTH_CONNECTION);

    loginWithRedirect({

      appState: {

        returnTo: invitationToken

          ? `/invitation/${invitationToken}`

          : "/post-login",

      },

      authorizationParams: {

        screen_hint: "login",

// Force Auth0 to ask for credentials instead of

// silently reusing an existing Homebuyer SSO session.

        prompt: "login",

        connection: HOMEBUYER_AUTH_CONNECTION,

        ...(form.email

          ? {

              login_hint: form.email,

            }

          : {}),

      },

    });

  }

/* Section */

  function goToLoginAfterRegistration() {

    sessionStorage.setItem(AUTH_CONNECTION_KEY, HOMEBUYER_AUTH_CONNECTION);

    loginWithRedirect({

      appState: {

        returnTo: "/post-login",

      },

      authorizationParams: {

        screen_hint: "login",

// Force Auth0 to ask for credentials instead of

// silently reusing an existing Homebuyer SSO session.

        prompt: "login",

        connection: HOMEBUYER_AUTH_CONNECTION,

        ...(form.email

          ? {

              login_hint: form.email,

            }

          : {}),

      },

    });

  }

/* Section */

  async function submit(e) {

    e.preventDefault();

    setErr("");

    setOk("");

/* Section */

    if (isInvitationRegistration && invitationError) {

      setErr(invitationError);

      return;

    }

/* Section */

    const errors = validateForm();

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {

      setErr(

        t(

          "validation.correctHighlightedFields",

          "Please correct the highlighted fields.",

        ),

      );

      return;

    }

/* Section */

    setLoading(true);

    try {

      const phonePrefix = getPhoneCodeByCountry(form.country_of_residence);

      const response = await fetch(`${API_BASE}/api/register-homebuyer/`, {

        method: "POST",

        headers: {

          "Content-Type": "application/json",

          Accept: "application/json",

        },

        body: JSON.stringify({

          email: form.email.trim().toLowerCase(),

          password: form.password,

          first_name: form.first_name.trim(),

          last_name: form.last_name.trim(),

          family_situation: form.family_situation,

          registration_concerns: form.registration_concerns,

          country_of_residence: form.country_of_residence,

          country_of_property_purchase: form.country_of_property_purchase,

          national_id_number:

            (form.country_of_residence || form.country_of_property_purchase) ===

            "BE"

              ? form.national_identification_number

                  .replace(/[.\s/-]/g, "")

                  .trim()

              : form.national_identification_number.trim(),

          phone: `${phonePrefix || ""} ${form.phone || ""}`.trim(),

          invitation_token: invitationToken || "",

        }),

      });

      const data = await response.json().catch(() => ({}));

/* Section */

      if (!response.ok) {

        if (data && typeof data === "object") {

          const backendFieldErrors = {};

          Object.entries(data).forEach(([key, value]) => {

            if (Array.isArray(value)) {

              backendFieldErrors[key] = value.join(" ");

            } else if (

              typeof value === "string" &&

              key !== "detail" &&

              key !== "message"

            ) {

              backendFieldErrors[key] = value;

            }

          });

          if (Object.keys(backendFieldErrors).length) {

            setFieldErrors((previous) => ({

              ...previous,

              ...backendFieldErrors,

            }));

          }

        }

        const backendMessage =

          data?.message ||

          data?.detail ||

          data?.invitation_token ||

          data?.email ||

          t("errors.registrationFailed", "Registration failed.");

        if (Array.isArray(backendMessage)) {

          setErr(backendMessage.join(" "));

        } else {

          setErr(String(backendMessage));

        }

        return;

      }

/* Section */

      setFieldErrors({});

      localStorage.removeItem(REGISTRATION_STORAGE_KEY);

      if (isInvitationRegistration) {

        setOk(

          t(

            "invitation.registrationComplete",

            "Your LEG-UP account has been created and connected to the loan case. Redirecting to login...",

          ),

        );

      } else {

        setOk(

          t(

            "registrationSuccess",

            "Registered successfully. Redirecting to login...",

          ),

        );

      }

      window.setTimeout(() => {

        goToLoginAfterRegistration();

      }, 1200);

    } catch (ex) {

      setErr(ex?.message || String(ex));

    } finally {

      setLoading(false);

    }

  }

  React.useEffect(() => {

    try {

      const formToSave = {

        ...form,

        password: "",

        confirm_password: "",

      };

      localStorage.setItem(

        REGISTRATION_STORAGE_KEY,

        JSON.stringify(formToSave),

      );

    } catch (error) {

      console.error("Failed to save registration form:", error);

    }

  }, [form]);

/* Section */

  return (

    <div className="hb-register-page">

      {/* Section */}

      <div

        className="hb-register-bg-illustration hb-register-reveal-bg"

        style={{

          backgroundImage: `url(${backgroundImg})`,

        }}

      />

      {/* Section */}

      <div className="hb-register-topbar hb-register-reveal hb-register-reveal--topbar">

        <Link to="/" className="hb-register-logo">

          <img src={logoImg} alt="LEG-UP" />

        </Link>

        <div className="hb-register-topbar__right">

          <LanguageSwitcher />

          <LoginButton />

        </div>

      </div>

      {/* Section */}

      <div className="hb-register-shell">

        <div className="hb-register-card hb-register-reveal hb-register-reveal--card">

          {/* Section */}

          <div className="hb-register-header hb-register-reveal-group">

            <p className="hb-register-kicker">{t("kicker")}</p>

            <h1>

              {isInvitationRegistration

                ? t("invitation.title", "Complete your LEG-UP registration")

                : t("title")}

            </h1>

            <p className="hb-register-subtitle">

              {isInvitationRegistration

                ? t(

                    "invitation.subtitle",

                    "Your lender has already prepared your loan case. Complete your account registration to access it.",

                  )

                : t("subtitle")}

            </p>

          </div>

          {/* Section */}

          {isInvitationRegistration && (

            <div className="hb-register-note hb-register-reveal hb-register-reveal--note">

              {loadingInvitation ? (

                <div

                  style={{

                    display: "flex",

                    alignItems: "center",

                    gap: "8px",

                  }}

                >

                  <LoaderCircle

                    size={17}

                    className="hb-invitation-page__spinner"

                  />

                  <span>

                    {t(

                      "invitation.loading",

                      "Loading information provided by your lender...",

                    )}

                  </span>

                </div>

              ) : invitationError ? (

                <span>{invitationError}</span>

              ) : (

                <div

                  style={{

                    display: "flex",

                    alignItems: "flex-start",

                    gap: "9px",

                  }}

                >

                  <ShieldCheck size={18} />

                  <span>

                    {t(

                      "invitation.prefilledNotice",

                      "Some fields have been prefilled using information already provided by your lender. Please review the information and complete the remaining fields.",

                    )}

                    {invitationData?.dossier_code

                      ? ` ${t(

                          "invitation.dossierReference",

                          "Dossier reference",

                        )}: ${invitationData.dossier_code}.`

                      : ""}

                  </span>

                </div>

              )}

            </div>

          )}

          {!isInvitationRegistration && (

            <div className="hb-register-note hb-register-reveal hb-register-reveal--note">

              {t("note")}

            </div>

          )}

          {/* Section */}

          <form

            className="hb-register-form-card hb-register-reveal hb-register-reveal--form"

            onSubmit={submit}
          onBlur={handleFieldBlur}
          noValidate

          >

            <h2 className="hb-section-title hb-register-reveal hb-register-reveal--section">

              {t("sectionTitle")}

            </h2>

            {/* Section */}

            <div className="hb-radio-row">

              {/* Section */}

              <div className="hb-radio-group hb-register-reveal-item">

                <label className="hb-field-label">

                  {t("registrationConcerns")}

                </label>

                <div className="hb-radio-options">

                  <label className="hb-radio-option">

                    <input

                      type="radio"

                      name="registration_concerns"

                      value="ONE_PERSON"

                      checked={form.registration_concerns === "ONE_PERSON"}

                      onChange={onChange}

                    />

                    <span>{t("onePerson")}</span>

                  </label>

                  <label className="hb-radio-option">

                    <input

                      type="radio"

                      name="registration_concerns"

                      value="TWO_PEOPLE"

                      checked={form.registration_concerns === "TWO_PEOPLE"}

                      onChange={onChange}

                    />

                    <span>{t("twoPeople")}</span>

                  </label>

                </div>

              </div>

              {/* Section */}

              <div className="hb-radio-group hb-register-reveal-item">

                <label className="hb-field-label">{t("familySituation")}</label>

                <div className="hb-radio-options">

                  <label className="hb-radio-option">

                    <input

                      type="radio"

                      name="family_situation"

                      value="SINGLE"

                      checked={form.family_situation === "SINGLE"}

                      onChange={onChange}

                    />

                    <span>{t("single")}</span>

                  </label>

                  <label className="hb-radio-option">

                    <input

                      type="radio"

                      name="family_situation"

                      value="COUPLE"

                      checked={form.family_situation === "COUPLE"}

                      onChange={onChange}

                    />

                    <span>{t("couple")}</span>

                  </label>

                </div>

              </div>

            </div>

            {/* Section */}

            <div className="hb-form-grid">

              {/* Section */}

              <div className="hb-field hb-register-reveal-item">

                <label className="hb-field-label">{t("firstName")}</label>

                <input

                  name="first_name"

                  placeholder={t("placeholders.firstName")}

                  value={form.first_name}

                  onChange={onChange}

                  readOnly={isInvitationRegistration}

                  className={[

                    fieldErrors.first_name ? "hb-input-error" : "",

                    isInvitationRegistration ? "hb-input-locked" : "",

                  ]

                    .filter(Boolean)

                    .join(" ")}


                />

                {isInvitationRegistration && (

                  <span className="hb-register-prefilled-hint">

                    {t(

                      "invitation.identityLocked",

                      "This information comes from the lender-created loan case.",

                    )}

                  </span>

                )}

                {fieldErrors.first_name && (

                  <span className="hb-field-error">

                    {fieldErrors.first_name}

                  </span>

                )}

              </div>

              {/* Section */}

              <div className="hb-field hb-register-reveal-item">

                <label className="hb-field-label">{t("lastName")}</label>

                <input

                  name="last_name"

                  placeholder={t("placeholders.lastName")}

                  value={form.last_name}

                  onChange={onChange}

                  readOnly={isInvitationRegistration}

                  className={[

                    fieldErrors.last_name ? "hb-input-error" : "",

                    isInvitationRegistration ? "hb-input-locked" : "",

                  ]

                    .filter(Boolean)

                    .join(" ")}


                />

                {isInvitationRegistration && (

                  <span className="hb-register-prefilled-hint">

                    {t(

                      "invitation.identityLocked",

                      "This information comes from the lender-created loan case.",

                    )}

                  </span>

                )}

                {fieldErrors.last_name && (

                  <span className="hb-field-error">

                    {fieldErrors.last_name}

                  </span>

                )}

              </div>

              {/* Section */}

              <div className="hb-field hb-register-reveal-item">

                <label className="hb-field-label">

                  {t("countryOfResidence")}

                </label>

                <select

                  name="country_of_residence"

                  value={form.country_of_residence}

                  onChange={onChange}

                  className={

                    fieldErrors.country_of_residence ? "hb-input-error" : ""

                  }

                >

                  <option value="">{t("common:selectCountry")}</option>

                  {EUROPEAN_COUNTRIES.map((country) => (

                    <option key={country.code} value={country.code}>

                      {country.label}

                    </option>

                  ))}

                </select>

                {fieldErrors.country_of_residence && (

                  <span className="hb-field-error">

                    {fieldErrors.country_of_residence}

                  </span>

                )}

              </div>

              {/* Section */}

              <div className="hb-field hb-register-reveal-item">

                <label className="hb-field-label">{t("nationalId")}</label>

                <input

                  name="national_identification_number"

                  placeholder={

                    NATIONAL_ID_EXAMPLES[

                      form.country_of_residence ||

                        form.country_of_property_purchase

                    ] ||

                    t(

                      "placeholders.nationalId",

                      "National identification number",

                    )

                  }

                  value={form.national_identification_number}

                  onChange={onChange}

                  className={

                    fieldErrors.national_identification_number

                      ? "hb-input-error"

                      : ""

                  }

                />

                {fieldErrors.national_identification_number && (

                  <span className="hb-field-error">

                    {fieldErrors.national_identification_number}

                  </span>

                )}

              </div>

              {/* Section */}

              <div className="hb-field hb-register-reveal-item">

                <label className="hb-field-label">{t("email")}</label>

                <input

                  name="email"

                  type="email"

                  placeholder={t("placeholders.email")}

                  value={form.email}

                  onChange={onChange}

                  readOnly={isInvitationRegistration}

                  className={[

                    fieldErrors.email ? "hb-input-error" : "",

                    isInvitationRegistration ? "hb-input-locked" : "",

                  ]

                    .filter(Boolean)

                    .join(" ")}

                />

                {fieldErrors.email && (

                  <span className="hb-field-error">{fieldErrors.email}</span>

                )}

              </div>

              {/* Section */}

              <div className="hb-field hb-register-reveal-item">

                <label className="hb-field-label">{t("confirmEmail")}</label>

                <input

                  name="confirm_email"

                  type="email"

                  placeholder={t("placeholders.confirmEmail")}

                  value={form.confirm_email}

                  onChange={onChange}

                  readOnly={isInvitationRegistration}

                  className={[

                    fieldErrors.confirm_email ? "hb-input-error" : "",

                    isInvitationRegistration ? "hb-input-locked" : "",

                  ]

                    .filter(Boolean)

                    .join(" ")}

                />

                {fieldErrors.confirm_email && (

                  <span className="hb-field-error">

                    {fieldErrors.confirm_email}

                  </span>

                )}

              </div>

              {/* Section */}

              <div className="hb-field hb-register-reveal-item">

                <label className="hb-field-label">{t("password")}</label>

                <div className="hb-password-wrap">

                  <input

                    name="password"

                    type={showPassword ? "text" : "password"}

                    placeholder={t("placeholders.password")}

                    value={form.password}

                    onChange={onChange}

                    className={fieldErrors.password ? "hb-input-error" : ""}

                  />

                  <button

                    type="button"

                    className="hb-password-toggle"

                    onClick={() => setShowPassword((previous) => !previous)}

                    aria-label={

                      showPassword

                        ? t("hidePassword", "Hide password")

                        : t("showPassword", "Show password")

                    }

                  >

                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}

                  </button>

                </div>

                <PasswordStrengthChecklist

                  password={form.password}

                  variant="light"

                />

                {fieldErrors.password && (

                  <span className="hb-field-error">{fieldErrors.password}</span>

                )}

              </div>

              {/* Section */}

              <div className="hb-field hb-register-reveal-item">

                <label className="hb-field-label">{t("confirmPassword")}</label>

                <div className="hb-password-wrap">

                  <input

                    name="confirm_password"

                    type={showConfirmPassword ? "text" : "password"}

                    placeholder={t("placeholders.confirmPassword")}

                    value={form.confirm_password}

                    onChange={onChange}

                    className={

                      fieldErrors.confirm_password ? "hb-input-error" : ""

                    }

                  />

                  <button

                    type="button"

                    className="hb-password-toggle"

                    onClick={() =>

                      setShowConfirmPassword((previous) => !previous)

                    }

                    aria-label={

                      showConfirmPassword

                        ? t("hidePassword", "Hide password")

                        : t("showPassword", "Show password")

                    }

                  >

                    {showConfirmPassword ? (

                      <EyeOff size={18} />

                    ) : (

                      <Eye size={18} />

                    )}

                  </button>

                </div>

                {fieldErrors.confirm_password && (

                  <span className="hb-field-error">

                    {fieldErrors.confirm_password}

                  </span>

                )}

              </div>

              {/* Section */}

              <div className="hb-field hb-register-reveal-item">

                <label className="hb-field-label">

                  {t("countryOfPropertyPurchase")}

                </label>

                <select

                  name="country_of_property_purchase"

                  value={form.country_of_property_purchase}

                  onChange={onChange}

                  disabled={isInvitationRegistration}

                  className={[

                    fieldErrors.country_of_property_purchase

                      ? "hb-input-error"

                      : "",

                    isInvitationRegistration ? "hb-input-locked" : "",

                  ]

                    .filter(Boolean)

                    .join(" ")}

                >

                  <option value="">{t("common:selectCountry")}</option>

                  {PROPERTY_PURCHASE_COUNTRIES.map((country) => (

                    <option key={country.code} value={country.code}>

                      {country.label}

                    </option>

                  ))}

                </select>

                {fieldErrors.country_of_property_purchase && (

                  <span className="hb-field-error">

                    {fieldErrors.country_of_property_purchase}

                  </span>

                )}

                {isInvitationRegistration && (

                  <span className="hb-register-prefilled-hint">

                    {t(

                      "invitation.propertyCountryLocked",

                      "This value comes from the lender-created loan case.",

                    )}

                  </span>

                )}

              </div>

              {/* Section */}

              <div className="hb-field hb-register-reveal-item">

                <label className="hb-field-label">{t("phone")}</label>

                <div className="hb-phone-field">

                  <span className="hb-phone-prefix">

                    {getPhoneCodeByCountry(form.country_of_residence) || "—"}

                  </span>

                  <input

                    name="phone"

                    placeholder={t("placeholders.phone")}

                    value={form.phone}

                    onChange={onChange}

                    className={fieldErrors.phone ? "hb-input-error" : ""}

                  />

                </div>

                {fieldErrors.phone && (

                  <span className="hb-field-error">{fieldErrors.phone}</span>

                )}

              </div>

            </div>

            {/* Section */}

            <div className="hb-terms-block hb-register-reveal hb-register-reveal--terms">

              <p className="hb-field-label">{t("termsLabel")}</p>

              <div className="hb-checkbox-row">

                <input

                  id="hb-agree-terms"

                  type="checkbox"

                  name="agree_terms"

                  checked={form.agree_terms}

                  onChange={onChange}

                />

                <label

                  htmlFor="hb-agree-terms"

                  className="hb-terms-consent-text"

                >

                  {t("termsCheckPrefix", "I agree to the")}

                </label>

                <button

                  type="button"

                  className="hb-terms-link"

                  onClick={() => setTermsModalOpen(true)}

                >

                  {t("termsModal.link", "Terms and Conditions")}

                </button>

              </div>

              {fieldErrors.agree_terms && (

                <span className="hb-field-error">

                  {fieldErrors.agree_terms}

                </span>

              )}

              <p className="hb-terms-help">{t("termsHelp")}</p>

            </div>

            {(err || ok) && (

              <div className="hb-message-area">

                {err && (

                  <div

                    className="hb-message hb-message-error"

                    style={{

                      whiteSpace: "normal",

                      overflowWrap: "anywhere",

                      maxHeight: "none",

                      height: "auto",

                      overflow: "visible",

                    }}

                  >

                    {err}

                  </div>

                )}

                {ok && (

                  <div className="hb-message hb-message-success">{ok}</div>

                )}

              </div>

            )}

            {/* Section */}

            <div className="hb-register-footer hb-register-reveal hb-register-reveal--footer">

              <p className="hb-login-link">

                {isInvitationRegistration

                  ? t(

                      "invitation.alreadyHaveAccount",

                      "Already have a LEG-UP account?",

                    )

                  : t("alreadyHaveAccount")}{" "}

                <button

                  type="button"

                  className="hb-login-auth-link"

                  onClick={goToLogin}

                >

                  {t("common:login")}

                </button>

              </p>

              <button

                className="hb-submit-btn"

                disabled={

                  loading || loadingInvitation || Boolean(invitationError)

                }

                type="submit"

              >

                {loading

                  ? t("creating")

                  : isInvitationRegistration

                    ? t("invitation.submit", "Create account and connect case")

                    : t("submit")}

              </button>

            </div>

          </form>

        </div>

      </div>

      <TermsConditionsModal open={termsModalOpen} onClose={closeTermsModal} />

    </div>

  );

}
